import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FaCheck,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaShieldAlt,
  FaTimes,
  FaUserCheck,
  FaEye,
  FaInfoCircle,
} from 'react-icons/fa';
import PolicyService from '../../services/api/policy';
import UtilService from '../../services/api/Utils';
import AgentService from '../../services/api/agents';
import { API_RESPONSE } from '../../settings/shared/constants';
import { PolicyJsonModal } from '../policy/PolicyJsonViewer';
import { APPROVAL_CHANNELS } from './approvalChannels';

const POLICY_PAGE_SIZE = 10;
const FILTER_PAGE_SIZE = PolicyService.POLICY_PAGE_SIZE || 50;

function policyKey(policy) {
  return String(
    policy?.policyId ||
    policy?.id ||
    policy?.name ||
    policy?.label ||
    ''
  ).trim();
}

function policyName(policy) {
  return String(
    policy?.name ||
    policy?.label ||
    policy?.policyId ||
    policy?.id ||
    'Untitled policy'
  ).trim();
}

function normalizePolicyPage(res, pageSize = 50) {
  const page = res?.policyResponseDTOPage || res?.data || res;

  const content = Array.isArray(page?.content)
    ? page.content
    : Array.isArray(res?.content)
      ? res.content
      : Array.isArray(page)
        ? page
        : [];

  const totalElements =
    Number(
      page?.totalElements ??
      res?.totalElements ??
      content.length
    ) || 0;

  const size =
    Number(page?.size ?? res?.size ?? pageSize) || pageSize;

  let totalPages =
    Number(page?.totalPages ?? res?.totalPages ?? 0) || 0;

  if (!totalPages && totalElements > 0) {
    totalPages = Math.max(
      1,
      Math.ceil(totalElements / size)
    );
  }

  return {
    content,
    totalPages,
    totalElements,
  };
}

function unwrapFilterValue(item) {
  if (item == null) return '';

  if (
    typeof item === 'string' ||
    typeof item === 'number'
  ) {
    return String(item).trim();
  }

  return String(
    item.name ||
    item.category ||
    item.subcategory ||
    item.subCategory ||
    item.label ||
    item.value ||
    ''
  ).trim();
}

function toFilterOptions(values = []) {
  return values
    .map(unwrapFilterValue)
    .filter(Boolean)
    .map((value) => ({
      value,
      label: value,
    }));
}

function normalizePolicyDocument(res) {
  if (res == null) return null;

  if (typeof res === 'string') {
    try {
      return JSON.parse(res);
    } catch {
      return { _raw: res };
    }
  }

  if (typeof res !== 'object') {
    return { value: res };
  }

  const nested =
    res.policyDocument ??
    res.policy ??
    res.content ??
    res.data ??
    res.body;

  if (
    nested != null &&
    typeof nested === 'object' &&
    !Array.isArray(nested)
  ) {
    if (!('content' in nested && 'pageable' in nested)) {
      return nested;
    }
  }

  if (typeof nested === 'string') {
    try {
      return JSON.parse(nested);
    } catch {
      return { _raw: nested };
    }
  }

  return res;
}

function extractConnectedApps(res) {
  const page =
    res?.thirdPartyAppsDTOPage ||
    res?.data ||
    res;

  if (Array.isArray(page?.content)) {
    return page.content;
  }

  if (Array.isArray(res?.content)) {
    return res.content;
  }

  if (Array.isArray(page)) {
    return page;
  }

  return [];
}

function matchesChannel(app, channel) {
  if (app?._channelHint === channel.id) {
    return true;
  }

  const hay =
    `${app?.provider || ''} ${app?.name || ''} ${app?.id || ''} ${app?.agentId || ''}`
      .toLowerCase();

  return channel.match.some((token) =>
    hay.includes(token)
  );
}

function channelHintForProvider(provider) {
  const key = String(provider || '').toUpperCase();

  if (key === 'SLACK') return 'slack';
  if (key === 'GMAIL') return 'gmail';

  return null;
}

function recipientFromApp(app) {
  if (!app || typeof app !== 'object') {
    return '';
  }

  const config =
    app.configuration ||
    app.additionalConfig ||
    app.config ||
    {};

  return String(
    app.channel_id ||
    app.channelId ||
    app.recipient ||
    app.userId ||
    config.channel_id ||
    config.channelId ||
    config.recipient ||
    ''
  ).trim();
}

function providersForChannel(channelId) {
  return (
    APPROVAL_CHANNELS.find(
      (channel) => channel.id === channelId
    )?.providers || []
  );
}

function buildPolicyEntry(source, agentId, document) {
  const id = policyKey(source);

  return {
    policyId: id,
    agentId,
    enabled: source?.enabled !== false,
    policy:
      document &&
        typeof document === 'object'
        ? document
        : source?.policy &&
          typeof source.policy === 'object'
          ? source.policy
          : {},
    name: policyName(source),
    s3Path: source?.s3Path || source?.path || '',
    category: source?.category || '',
    subcategory:
      source?.subcategory ||
      source?.subCategory ||
      '',
  };
}

export default function AgentAdvancedControls({
  node,
  onChange,
}) {
  const dataRef = useRef(node?.data);
  dataRef.current = node?.data;

  const selectedPolicies = Array.isArray(
    node?.data?.policies
  )
    ? node.data.policies
    : [];

  const selectedKeys = useMemo(
    () =>
      new Set(
        selectedPolicies
          .map(policyKey)
          .filter(Boolean)
      ),
    [selectedPolicies]
  );

  const approvalChannels = Array.isArray(
    node?.data?.approvalChannels
  )
    ? node.data.approvalChannels
    : [];

  const humanApproval =
    !!node?.data?.humanApproval;

  const selectedChannel =
    APPROVAL_CHANNELS.find(
      (channel) =>
        channel.id ===
        (
          node?.data?.approvalChannelType ||
          approvalChannels[0]?.channel ||
          ''
        )
    ) || null;

  /*
   * OAuth is required only when a channel type
   * has been selected.
   *
   * Channel Type itself is NOT required.
   */
  const selectedOAuth = approvalChannels.find(
    (item) =>
      item.channel === selectedChannel?.id
  );

  const oauthRequired =
    !!selectedChannel &&
    !selectedOAuth?.appId;

  /*
   * Recipient is required for BOTH Slack and Gmail
   * when a channel type is selected.
   */
  const recipientRequired =
    !!selectedChannel &&
    !(node?.data?.approvalRecipient || '').trim();

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] =
    useState(false);
  const [catalogError, setCatalogError] =
    useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] =
    useState('');
  const [subcategoryFilter, setSubcategoryFilter] =
    useState('');
  const [categories, setCategories] =
    useState([]);
  const [subcategories, setSubcategories] =
    useState([]);
  const [categoriesLoading, setCategoriesLoading] =
    useState(false);
  const [
    subcategoriesLoading,
    setSubcategoriesLoading,
  ] = useState(false);
  const [policyPage, setPolicyPage] =
    useState(0);
  const [
    policyTotalPages,
    setPolicyTotalPages,
  ] = useState(0);
  const [policyTotal, setPolicyTotal] =
    useState(0);
  const [dropdownOpen, setDropdownOpen] =
    useState(false);
  const [dropdownPos, setDropdownPos] =
    useState(null);
  const [previewMeta, setPreviewMeta] =
    useState(null);
  const [
    previewDocument,
    setPreviewDocument,
  ] = useState(null);
  const [previewLoading, setPreviewLoading] =
    useState(false);
  const [previewError, setPreviewError] =
    useState(null);
  const [loadingDocs, setLoadingDocs] =
    useState({});
  const [connectedApps, setConnectedApps] =
    useState([]);
  const [appsLoading, setAppsLoading] =
    useState(false);

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const patchData = useCallback(
    (partial) => {
      onChange({
        ...dataRef.current,
        ...partial,
      });
    },
    [onChange]
  );

  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      setCategoriesLoading(true);

      try {
        const res =
          await PolicyService.getCategories(
            0,
            FILTER_PAGE_SIZE
          );

        if (cancelled) return;

        if (
          res?.status === API_RESPONSE.FAILED
        ) {
          setCategories([]);
          return;
        }

        const { content } =
          normalizePolicyPage(
            res,
            FILTER_PAGE_SIZE
          );

        setCategories(
          toFilterOptions(content).map(
            (item) => item.value
          )
        );
      } catch {
        if (!cancelled) {
          setCategories([]);
        }
      } finally {
        if (!cancelled) {
          setCategoriesLoading(false);
        }
      }
    };

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!categoryFilter) {
      setSubcategories([]);
      setSubcategoriesLoading(false);
      return undefined;
    }

    const loadSubcategories = async () => {
      setSubcategoriesLoading(true);

      try {
        const res =
          await PolicyService.getSubcategories(
            categoryFilter,
            0,
            FILTER_PAGE_SIZE
          );

        if (cancelled) return;

        if (
          res?.status === API_RESPONSE.FAILED
        ) {
          setSubcategories([]);
          return;
        }

        const { content } =
          normalizePolicyPage(
            res,
            FILTER_PAGE_SIZE
          );

        setSubcategories(
          toFilterOptions(content).map(
            (item) => item.value
          )
        );
      } catch {
        if (!cancelled) {
          setSubcategories([]);
        }
      } finally {
        if (!cancelled) {
          setSubcategoriesLoading(false);
        }
      }
    };

    loadSubcategories();

    return () => {
      cancelled = true;
    };
  }, [categoryFilter]);

  useEffect(() => {
    let cancelled = false;

    const loadPolicies = async () => {
      setCatalogLoading(true);
      setCatalogError('');

      try {
        const filters = {};

        if (categoryFilter) {
          filters.category = categoryFilter;
        }

        if (subcategoryFilter) {
          filters.subcategory =
            subcategoryFilter;
        }

        const res =
          await PolicyService.getPolicies(
            policyPage,
            POLICY_PAGE_SIZE,
            filters
          );

        if (cancelled) return;

        if (
          res?.status === API_RESPONSE.FAILED
        ) {
          throw new Error(
            res?.errorMessage ||
            'Failed to load policies'
          );
        }

        const parsed =
          normalizePolicyPage(
            res,
            POLICY_PAGE_SIZE
          );

        setCatalog(parsed.content);
        setPolicyTotalPages(
          parsed.totalPages
        );
        setPolicyTotal(
          parsed.totalElements
        );
      } catch (err) {
        if (!cancelled) {
          setCatalog([]);
          setPolicyTotalPages(0);
          setPolicyTotal(0);
          setCatalogError(
            err?.message ||
            'Failed to load policies'
          );
        }
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      }
    };

    loadPolicies();

    return () => {
      cancelled = true;
    };
  }, [
    categoryFilter,
    subcategoryFilter,
    policyPage,
  ]);

  useEffect(() => {
    if (
      policyTotalPages > 0 &&
      policyPage > policyTotalPages - 1
    ) {
      setPolicyPage(
        Math.max(
          0,
          policyTotalPages - 1
        )
      );
    }
  }, [
    policyPage,
    policyTotalPages,
  ]);

  useEffect(() => {
    let cancelled = false;

    const channelId =
      selectedChannel?.id;

    if (!humanApproval || !channelId) {
      setConnectedApps([]);
      setAppsLoading(false);
      return undefined;
    }

    const loadApps = async () => {
      setAppsLoading(true);

      try {
        const seen = new Map();

        const addApps = (
          list,
          hint
        ) => {
          (list || []).forEach((app) => {
            const id = String(
              app?.id ||
              `${app?.provider}-${app?.name}`
            );

            if (!id) return;

            const next = hint
              ? {
                ...app,
                _channelHint: hint,
              }
              : app;

            const prev = seen.get(id);

            seen.set(
              id,
              prev?._channelHint
                ? prev
                : next
            );
          });
        };

        const providers =
          providersForChannel(
            channelId
          );

        const providerResults =
          await Promise.allSettled(
            providers.map(
              async (provider) => ({
                provider,
                apps: extractConnectedApps(
                  await AgentService.getConnectedApps(
                    provider
                  )
                ),
              })
            )
          );

        providerResults.forEach(
          (result) => {
            if (
              result.status !==
              'fulfilled'
            ) {
              return;
            }

            addApps(
              result.value.apps,
              channelHintForProvider(
                result.value.provider
              ) || channelId
            );
          }
        );

        const channel =
          APPROVAL_CHANNELS.find(
            (item) =>
              item.id === channelId
          );

        const filtered = [
          ...seen.values(),
        ].filter((app) =>
          matchesChannel(
            app,
            channel
          )
        );

        if (!cancelled) {
          setConnectedApps(
            filtered
          );
        }
      } catch {
        if (!cancelled) {
          setConnectedApps([]);
        }
      } finally {
        if (!cancelled) {
          setAppsLoading(false);
        }
      }
    };

    loadApps();

    return () => {
      cancelled = true;
    };
  }, [
    humanApproval,
    selectedChannel?.id,
  ]);

  const filteredCatalog = useMemo(() => {
    const q = search
      .trim()
      .toLowerCase();

    if (!q) return catalog;

    return catalog.filter((policy) => {
      const hay = [
        policy.name,
        policy.label,
        policy.policyId,
        policy.category,
        policy.subcategory,
        policy.description,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return hay.includes(q);
    });
  }, [catalog, search]);

  const positionDropdown = useCallback(() => {
    const el = triggerRef.current;

    if (!el) return;

    const rect =
      el.getBoundingClientRect();

    const gap = 6;
    const maxHeight = 280;

    const spaceBelow =
      window.innerHeight -
      rect.bottom -
      12;

    const openUp =
      spaceBelow < 180 &&
      rect.top > spaceBelow;

    setDropdownPos({
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(
        140,
        Math.min(
          maxHeight,
          openUp
            ? rect.top - 16
            : spaceBelow
        )
      ),
      ...(openUp
        ? {
          bottom:
            window.innerHeight -
            rect.top +
            gap,
        }
        : {
          top:
            rect.bottom + gap,
        }),
    });
  }, []);

  useEffect(() => {
    if (!dropdownOpen) {
      setDropdownPos(null);
      return undefined;
    }

    positionDropdown();

    const update = () =>
      positionDropdown();

    window.addEventListener(
      'resize',
      update
    );

    window.addEventListener(
      'scroll',
      update,
      true
    );

    return () => {
      window.removeEventListener(
        'resize',
        update
      );

      window.removeEventListener(
        'scroll',
        update,
        true
      );
    };
  }, [
    dropdownOpen,
    positionDropdown,
    categoryFilter,
    subcategoryFilter,
    policyPage,
    catalogLoading,
  ]);

  useEffect(() => {
    if (!dropdownOpen) {
      return undefined;
    }

    const onDown = (event) => {
      if (
        triggerRef.current?.contains(
          event.target
        ) ||
        dropdownRef.current?.contains(
          event.target
        )
      ) {
        return;
      }

      setDropdownOpen(false);
      setSearch('');
    };

    document.addEventListener(
      'mousedown',
      onDown
    );

    return () =>
      document.removeEventListener(
        'mousedown',
        onDown
      );
  }, [dropdownOpen]);

  useEffect(() => {
    if (
      dropdownOpen &&
      searchRef.current
    ) {
      searchRef.current.focus();
    }

    if (!dropdownOpen) {
      setSearch('');
    }
  }, [dropdownOpen]);

  const inflightDocsRef =
    useRef(new Set());

  const loadPolicyDocument =
    useCallback(
      async (entry) => {
        const key = policyKey(entry);

        if (!key) return;

        if (
          entry?.policy &&
          typeof entry.policy ===
          'object' &&
          Object.keys(entry.policy)
            .length > 0
        ) {
          return;
        }

        const s3Path =
          entry?.s3Path;

        if (
          !s3Path ||
          inflightDocsRef.current.has(
            key
          )
        ) {
          return;
        }

        inflightDocsRef.current.add(
          key
        );

        setLoadingDocs((prev) => ({
          ...prev,
          [key]: true,
        }));

        try {
          const res =
            await UtilService.getPolicyByPath(
              s3Path
            );

          if (
            res?.status ===
            API_RESPONSE.FAILED
          ) {
            return;
          }

          const doc =
            normalizePolicyDocument(
              res
            );

          if (!doc) return;

          const current =
            Array.isArray(
              dataRef.current?.policies
            )
              ? dataRef.current.policies
              : [];

          patchData({
            policies: current.map(
              (item) =>
                policyKey(item) === key
                  ? {
                    ...item,
                    policy: doc,
                  }
                  : item
            ),
          });
        } finally {
          inflightDocsRef.current.delete(
            key
          );

          setLoadingDocs((prev) => ({
            ...prev,
            [key]: false,
          }));
        }
      },
      [patchData]
    );

  useEffect(() => {
    selectedPolicies.forEach((item) => {
      const hasDoc =
        item?.policy &&
        typeof item.policy ===
        'object' &&
        Object.keys(item.policy)
          .length > 0;

      if (
        !hasDoc &&
        item?.s3Path
      ) {
        loadPolicyDocument(item);
      }
    });
  }, [
    selectedPolicies,
    loadPolicyDocument,
  ]);

  const togglePolicy = useCallback(
    (policy) => {
      const key =
        policyKey(policy);

      if (!key) return;

      const current =
        Array.isArray(
          dataRef.current?.policies
        )
          ? dataRef.current.policies
          : [];

      const exists =
        current.some(
          (item) =>
            policyKey(item) === key
        );

      if (exists) {
        patchData({
          policies:
            current.filter(
              (item) =>
                policyKey(item) !==
                key
            ),
        });

        return;
      }

      const entry =
        buildPolicyEntry(
          policy,
          node.id,
          policy.policyDocument ||
          policy.policy
        );

      patchData({
        policies: [
          ...current,
          entry,
        ],
      });

      loadPolicyDocument(entry);
    },
    [
      loadPolicyDocument,
      node?.id,
      patchData,
    ]
  );

  const removePolicy =
    useCallback(
      (key) => {
        const current =
          Array.isArray(
            dataRef.current?.policies
          )
            ? dataRef.current.policies
            : [];

        patchData({
          policies:
            current.filter(
              (item) =>
                policyKey(item) !==
                key
            ),
        });

        setPreviewMeta(
          (current) =>
            current?.key === key
              ? null
              : current
        );
      },
      [patchData]
    );

  const closePreview =
    useCallback(() => {
      setPreviewMeta(null);
      setPreviewDocument(null);
      setPreviewLoading(false);
      setPreviewError(null);
    }, []);

  const openPreview =
    useCallback(
      (policy, event) => {
        event?.stopPropagation();

        const key =
          policyKey(policy);

        if (!key) return;

        const latest =
          (
            Array.isArray(
              dataRef.current?.policies
            )
              ? dataRef.current.policies
              : []
          ).find(
            (item) =>
              policyKey(item) === key
          ) || policy;

        setDropdownOpen(false);
        setPreviewDocument(null);
        setPreviewError(null);
        setPreviewLoading(true);

        setPreviewMeta({
          key,
          name: policyName(latest),
          s3Path:
            latest.s3Path ||
            policy.s3Path ||
            '',
          policy: latest.policy,
        });
      },
      []
    );

  useEffect(() => {
    if (!previewMeta) {
      return undefined;
    }

    let cancelled = false;

    const showDocument = (doc) => {
      requestAnimationFrame(() => {
        if (cancelled) return;

        if (
          !doc ||
          (
            typeof doc ===
            'object' &&
            !Object.keys(doc).length
          )
        ) {
          setPreviewError(
            'Policy document was empty'
          );

          setPreviewLoading(false);

          return;
        }

        setPreviewDocument(doc);
        setPreviewLoading(false);
      });
    };

    const cached =
      previewMeta.policy &&
        typeof previewMeta.policy ===
        'object' &&
        Object.keys(
          previewMeta.policy
        ).length > 0
        ? previewMeta.policy
        : null;

    if (cached) {
      showDocument(cached);

      return () => {
        cancelled = true;
      };
    }

    const s3Path =
      previewMeta.s3Path;

    if (!s3Path) {
      showDocument(null);

      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const res =
          await UtilService.getPolicyByPath(
            s3Path
          );

        if (cancelled) return;

        if (
          res?.status ===
          API_RESPONSE.FAILED
        ) {
          setPreviewError(
            res?.errorMessage ||
            'Failed to load policy document'
          );

          setPreviewLoading(false);

          return;
        }

        const doc =
          normalizePolicyDocument(
            res
          );

        showDocument(doc);

        if (doc) {
          const current =
            Array.isArray(
              dataRef.current?.policies
            )
              ? dataRef.current.policies
              : [];

          patchData({
            policies: current.map(
              (item) =>
                policyKey(item) ===
                  previewMeta.key
                  ? {
                    ...item,
                    policy: doc,
                  }
                  : item
            ),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setPreviewError(
            err?.message ||
            'Failed to load policy document'
          );

          setPreviewLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    previewMeta,
    patchData,
  ]);

  const setHumanApproval =
    useCallback(
      (enabled) => {
        patchData({
          humanApproval:
            enabled,

          ...(!enabled
            ? {
              approvalChannelType:
                '',
              approvalChannels:
                [],
              approvalRecipient:
                '',
            }
            : {}),
        });
      },
      [patchData]
    );

  const setChannelType =
    useCallback(
      (channelId) => {
        patchData({
          humanApproval: true,
          approvalChannelType:
            channelId,

          /*
           * Reset OAuth and Recipient
           * whenever the channel changes.
           */
          approvalChannels: [],
          approvalRecipient: '',
        });
      },
      [patchData]
    );

  const setOAuthApp =
    useCallback(
      (channelId, appId) => {
        const app =
          connectedApps.find(
            (item) =>
              String(item.id) ===
              String(appId)
          );

        const provider =
          String(
            app?.provider ||
            channelId ||
            ''
          ).toLowerCase();

        patchData({
          humanApproval: true,

          approvalChannelType:
            channelId,

          approvalChannels: app
            ? [
              {
                channel:
                  channelId,
                appId: app.id,
                id: app.id,
                name:
                  app.name ||
                  app.displayName ||
                  channelId,
                hasOauth: true,
                provider,
              },
            ]
            : [],

          /*
           * Keep the existing recipient
           * for BOTH Slack and Gmail.
           *
           * Previously this was only done
           * for Slack.
           */
          approvalRecipient: app
            ? (
              dataRef.current
                ?.approvalRecipient ||
              recipientFromApp(app)
            )
            : '',
        });
      },
      [
        connectedApps,
        patchData,
      ]
    );

  const setApprovalRecipient =
    useCallback(
      (value) => {
        patchData({
          approvalRecipient:
            value,
        });
      },
      [patchData]
    );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        marginTop: 16,
      }}
    >
      <div
        style={{
          border:
            '1px solid #e8edf5',
          borderRadius: 14,
          background:
            'linear-gradient(180deg, #fbfcff 0%, #ffffff 100%)',
          padding: 12,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 8,
          }}
        >
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 8,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                'rgba(72, 128, 255, 0.12)',
              color: '#3b6ef5',
            }}
          >
            <FaShieldAlt size={12} />
          </span>

          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: '#1e293b',
              }}
            >
              Policies
            </div>

            <div
              style={{
                fontSize: 11,
                color: '#64748b',
              }}
            >
              Filter by category and
              subcategory, then select
              policies
            </div>
          </div>
        </div>

        <button
          ref={triggerRef}
          type="button"
          onClick={() =>
            setDropdownOpen(
              (open) => !open
            )
          }
          style={{
            width: '100%',
            minHeight: 42,
            padding: '8px 10px',
            borderRadius: 10,
            border: `1px solid ${dropdownOpen
                ? '#7c9dff'
                : '#e2e8f0'
              }`,
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            boxShadow: dropdownOpen
              ? '0 0 0 3px rgba(72,128,255,0.12)'
              : 'none',
            textAlign: 'left',
          }}
        >
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontSize: 13,
                color:
                  selectedPolicies.length
                    ? '#1e293b'
                    : '#94a3b8',
              }}
            >
              {selectedPolicies.length ===
                0
                ? 'Select policies'
                : `${selectedPolicies.length} ${selectedPolicies.length ===
                  1
                  ? 'policy'
                  : 'policies'
                } selected`}
            </span>
          </div>

          <FaChevronDown
            size={11}
            style={{
              color: '#64748b',
              transform: dropdownOpen
                ? 'rotate(180deg)'
                : 'none',
              transition:
                'transform 0.15s ease',
            }}
          />
        </button>

        {dropdownOpen &&
          dropdownPos &&
          createPortal(
            <div
              ref={dropdownRef}
              style={{
                position: 'fixed',
                left: dropdownPos.left,
                width: dropdownPos.width,
                top: dropdownPos.top,
                bottom:
                  dropdownPos.bottom,
                zIndex: 10050,
                background: '#fff',
                border:
                  '1px solid #e2e8f0',
                borderRadius: 12,
                boxShadow:
                  '0 12px 32px rgba(15, 23, 42, 0.14)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection:
                  'column',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding:
                    '8px 10px',
                  borderBottom:
                    '1px solid #f1f5f9',
                }}
              >
                <FaSearch
                  size={11}
                  style={{
                    color:
                      '#94a3b8',
                  }}
                />

                <input
                  ref={searchRef}
                  value={search}
                  onChange={(event) =>
                    setSearch(
                      event.target.value
                    )
                  }
                  placeholder="Search by policy name"
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    fontSize: 13,
                    background:
                      'transparent',
                  }}
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection:
                    'column',
                  gap: 6,
                  padding:
                    '8px 10px',
                  borderBottom:
                    '1px solid #f1f5f9',
                }}
              >
                <select
                  className="form-select font13"
                  value={
                    categoryFilter
                  }
                  onMouseDown={(event) =>
                    event.stopPropagation()
                  }
                  onChange={(event) => {
                    setCategoryFilter(
                      event.target.value
                    );
                    setSubcategoryFilter(
                      ''
                    );
                    setPolicyPage(0);
                    setSearch('');
                  }}
                  style={{
                    width: '100%',
                    padding:
                      '6px 10px',
                    borderRadius: 8,
                    fontSize: 12,
                    border:
                      '1px solid #e2e8f0',
                  }}
                >
                  <option value="">
                    {categoriesLoading
                      ? 'Loading categories…'
                      : 'All categories'}
                  </option>

                  {categories.map(
                    (category) => (
                      <option
                        key={category}
                        value={
                          category
                        }
                      >
                        {category}
                      </option>
                    )
                  )}
                </select>

                <select
                  className="form-select font13"
                  value={
                    subcategoryFilter
                  }
                  disabled={
                    !categoryFilter
                  }
                  onMouseDown={(event) =>
                    event.stopPropagation()
                  }
                  onChange={(event) => {
                    setSubcategoryFilter(
                      event.target.value
                    );
                    setPolicyPage(0);
                    setSearch('');
                  }}
                  style={{
                    width: '100%',
                    padding:
                      '6px 10px',
                    borderRadius: 8,
                    fontSize: 12,
                    border:
                      '1px solid #e2e8f0',
                    opacity:
                      categoryFilter
                        ? 1
                        : 0.55,
                  }}
                >
                  <option value="">
                    {!categoryFilter
                      ? 'Select a category first'
                      : subcategoriesLoading
                        ? 'Loading subcategories…'
                        : 'All subcategories'}
                  </option>

                  {subcategories.map(
                    (subcategory) => (
                      <option
                        key={
                          subcategory
                        }
                        value={
                          subcategory
                        }
                      >
                        {subcategory}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div
                style={{
                  maxHeight:
                    dropdownPos.maxHeight,
                  overflowY: 'auto',
                  flex: 1,
                }}
              >
                {catalogLoading ? (
                  <div
                    style={{
                      padding: 14,
                      fontSize: 12,
                      color:
                        '#64748b',
                    }}
                  >
                    Loading policies…
                  </div>
                ) : catalogError ? (
                  <div
                    style={{
                      padding: 14,
                      fontSize: 12,
                      color:
                        '#dc2626',
                    }}
                  >
                    {catalogError}
                  </div>
                ) : filteredCatalog.length ===
                  0 ? (
                  <div
                    style={{
                      padding: 14,
                      fontSize: 12,
                      color:
                        '#64748b',
                    }}
                  >
                    No policies found
                  </div>
                ) : (
                  filteredCatalog.map(
                    (policy) => {
                      const key =
                        policyKey(
                          policy
                        );

                      const selected =
                        selectedKeys.has(
                          key
                        );

                      const meta = [
                        policy.category,
                        policy.subcategory ||
                        policy.subCategory,
                      ]
                        .filter(Boolean)
                        .join(' / ');

                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() =>
                            togglePolicy(
                              policy
                            )
                          }
                          style={{
                            width: '100%',
                            border: 'none',
                            borderBottom:
                              '1px solid #f8fafc',
                            background:
                              selected
                                ? '#f5f8ff'
                                : '#fff',
                            padding:
                              '10px 12px',
                            display:
                              'flex',
                            alignItems:
                              'center',
                            gap: 10,
                            cursor:
                              'pointer',
                            textAlign:
                              'left',
                          }}
                        >
                          <span
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 4,
                              border:
                                selected
                                  ? 'none'
                                  : '1.5px solid #cbd5e1',
                              background:
                                selected
                                  ? '#3b6ef5'
                                  : '#fff',
                              color: '#fff',
                              display:
                                'inline-flex',
                              alignItems:
                                'center',
                              justifyContent:
                                'center',
                              flexShrink: 0,
                            }}
                          >
                            {selected ? (
                              <FaCheck
                                size={8}
                              />
                            ) : null}
                          </span>

                          <span
                            style={{
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            <span
                              style={{
                                display:
                                  'block',
                                fontSize: 13,
                                fontWeight: 600,
                                color:
                                  '#1e293b',
                              }}
                            >
                              {policyName(
                                policy
                              )}
                            </span>

                            {meta ? (
                              <span
                                style={{
                                  display:
                                    'block',
                                  fontSize: 11,
                                  color:
                                    '#64748b',
                                }}
                              >
                                {meta}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      );
                    }
                  )
                )}
              </div>

              {policyTotalPages >
                0 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems:
                        'center',
                      justifyContent:
                        'space-between',
                      gap: 8,
                      padding:
                        '8px 10px',
                      borderTop:
                        '1px solid #f1f5f9',
                      background:
                        '#f8fafc',
                    }}
                  >
                    <button
                      type="button"
                      disabled={
                        policyPage <=
                        0 ||
                        catalogLoading
                      }
                      onClick={() =>
                        setPolicyPage(
                          (page) =>
                            Math.max(
                              0,
                              page - 1
                            )
                        )
                      }
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        border:
                          '1px solid #e2e8f0',
                        background:
                          '#fff',
                        color:
                          policyPage <=
                            0
                            ? '#cbd5e1'
                            : '#334155',
                        display:
                          'inline-flex',
                        alignItems:
                          'center',
                        justifyContent:
                          'center',
                        cursor:
                          policyPage <=
                            0
                            ? 'default'
                            : 'pointer',
                      }}
                    >
                      <FaChevronLeft
                        size={10}
                      />
                    </button>

                    <div
                      style={{
                        fontSize: 11,
                        color:
                          '#64748b',
                        textAlign:
                          'center',
                        lineHeight: 1.35,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color:
                            '#334155',
                        }}
                      >
                        Page{' '}
                        {policyPage +
                          1}{' '}
                        of{' '}
                        {Math.max(
                          1,
                          policyTotalPages
                        )}
                      </div>

                      <div>
                        {policyTotal}{' '}
                        policies
                      </div>
                    </div>

                    <button
                      type="button"
                      disabled={
                        policyPage >=
                        policyTotalPages -
                        1 ||
                        catalogLoading
                      }
                      onClick={() =>
                        setPolicyPage(
                          (page) =>
                            page + 1
                        )
                      }
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        border:
                          '1px solid #e2e8f0',
                        background:
                          '#fff',
                        color:
                          policyPage >=
                            policyTotalPages -
                            1
                            ? '#cbd5e1'
                            : '#334155',
                        display:
                          'inline-flex',
                        alignItems:
                          'center',
                        justifyContent:
                          'center',
                        cursor:
                          policyPage >=
                            policyTotalPages -
                            1
                            ? 'default'
                            : 'pointer',
                      }}
                    >
                      <FaChevronRight
                        size={10}
                      />
                    </button>
                  </div>
                )}
            </div>,
            document.body
          )}

        {selectedPolicies.length >
          0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginTop: 10,
              }}
            >
              {selectedPolicies.map(
                (policy) => {
                  const key =
                    policyKey(
                      policy
                    );

                  const loading =
                    previewLoading &&
                    previewMeta?.key ===
                    key;

                  return (
                    <span
                      key={key}
                      style={{
                        display:
                          'inline-flex',
                        alignItems:
                          'center',
                        gap: 6,
                        maxWidth:
                          '100%',
                        padding:
                          '4px 8px',
                        borderRadius:
                          999,
                        background:
                          '#eef4ff',
                        color:
                          '#334155',
                        fontSize: 12,
                        fontWeight: 600,
                        border:
                          '1px solid #dbe7ff',
                      }}
                    >
                      <span
                        style={{
                          overflow:
                            'hidden',
                          textOverflow:
                            'ellipsis',
                          whiteSpace:
                            'nowrap',
                          maxWidth: 140,
                        }}
                      >
                        {policyName(
                          policy
                        )}
                      </span>

                      <button
                        type="button"
                        title="Preview policy"
                        onClick={(
                          event
                        ) =>
                          openPreview(
                            policy,
                            event
                          )
                        }
                        style={{
                          border: 'none',
                          background:
                            'transparent',
                          padding: 0,
                          display:
                            'inline-flex',
                          alignItems:
                            'center',
                          color:
                            loading
                              ? '#94a3b8'
                              : '#3b6ef5',
                          cursor:
                            'pointer',
                        }}
                      >
                        <FaEye
                          size={12}
                        />
                      </button>

                      <button
                        type="button"
                        title="Remove policy"
                        onClick={() =>
                          removePolicy(
                            key
                          )
                        }
                        style={{
                          border: 'none',
                          background:
                            'transparent',
                          padding: 0,
                          display:
                            'inline-flex',
                          alignItems:
                            'center',
                          color:
                            '#64748b',
                          cursor:
                            'pointer',
                        }}
                      >
                        <FaTimes
                          size={9}
                        />
                      </button>
                    </span>
                  );
                }
              )}
            </div>
          )}
      </div>

      <div
        style={{
          border: humanApproval
            ? '1px solid rgba(34, 197, 94, 0.28)'
            : '1px solid #e8edf5',
          borderRadius: 14,
          background:
            humanApproval
              ? 'linear-gradient(180deg, rgba(34,197,94,0.08) 0%, #ffffff 42%)'
              : 'linear-gradient(180deg, #fbfcff 0%, #ffffff 100%)',
          padding: 12,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems:
              'flex-start',
            justifyContent:
              'space-between',
            gap: 10,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems:
                'flex-start',
              gap: 8,
              minWidth: 0,
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: 8,
                display:
                  'inline-flex',
                alignItems:
                  'center',
                justifyContent:
                  'center',
                background:
                  humanApproval
                    ? 'rgba(34, 197, 94, 0.16)'
                    : 'rgba(72, 128, 255, 0.12)',
                color:
                  humanApproval
                    ? '#15803d'
                    : '#3b6ef5',
                flexShrink: 0,
              }}
            >
              <FaUserCheck
                size={12}
              />
            </span>

            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color:
                    '#1e293b',
                }}
              >
                Human Approval
              </div>

              <div
                style={{
                  fontSize: 11,
                  color:
                    '#64748b',
                  marginTop: 2,
                  lineHeight: 1.4,
                }}
              >
                Pause at this step
                until a person
                approves. Workflow
                continues only
                after approval.
              </div>
            </div>
          </div>

          <label
            style={{
              position:
                'relative',
              display:
                'inline-flex',
              cursor:
                'pointer',
              flexShrink: 0,
            }}
          >
            <input
              type="checkbox"
              checked={
                humanApproval
              }
              onChange={(
                event
              ) =>
                setHumanApproval(
                  event.target
                    .checked
                )
              }
              style={{
                position:
                  'absolute',
                opacity: 0,
                width: 0,
                height: 0,
              }}
            />

            <span
              style={{
                width: 40,
                height: 22,
                borderRadius:
                  999,
                background:
                  humanApproval
                    ? '#22c55e'
                    : '#cbd5e1',
                position:
                  'relative',
                transition:
                  'background 0.15s ease',
              }}
            >
              <span
                style={{
                  position:
                    'absolute',
                  top: 3,
                  left:
                    humanApproval
                      ? 20
                      : 3,
                  width: 16,
                  height: 16,
                  borderRadius:
                    '50%',
                  background:
                    '#fff',
                  boxShadow:
                    '0 1px 3px rgba(0,0,0,0.18)',
                  transition:
                    'left 0.15s ease',
                }}
              />
            </span>
          </label>
        </div>

        {humanApproval && (
          <div
            style={{
              marginTop: 12,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems:
                  'flex-start',
                gap: 8,
                padding:
                  '8px 10px',
                borderRadius: 10,
                background:
                  '#f0fdf4',
                border:
                  '1px solid #bbf7d0',
                marginBottom:
                  12,
              }}
            >
              <FaInfoCircle
                size={12}
                style={{
                  color:
                    '#16a34a',
                  marginTop: 2,
                  flexShrink: 0,
                }}
              />

              <div
                style={{
                  fontSize: 11,
                  color:
                    '#166534',
                  lineHeight: 1.45,
                }}
              >
                {selectedChannel
                  ? `Approval is sent on ${selectedChannel.label} and also to your default email.`
                  : 'If no channel type is selected, approval requests will be sent to your default email address..'}
              </div>
            </div>

            <label
              className="font13"
              style={{
                color:
                  '#4a5568',
                marginBottom: 6,
                display: 'block',
              }}
            >
              Channel type
            </label>

            <div
              style={{
                position:
                  'relative',
              }}
            >
              {/*
               * Channel Type is intentionally
               * NOT required.
               */}
              <select
                className="form-select font13"
                value={
                  selectedChannel?.id ||
                  ''
                }
                onChange={(
                  event
                ) =>
                  setChannelType(
                    event.target
                      .value
                  )
                }
                style={{
                  width: '100%',
                  padding:
                    '9px 12px',
                  paddingRight:
                    32,
                  borderRadius: 10,
                  fontSize: 13,
                  border:
                    '1px solid #e2e8f0',
                  background:
                    '#fff',
                }}
              >
                <option value="">
                  Select channel type
                </option>

                {APPROVAL_CHANNELS.map(
                  (channel) => (
                    <option
                      key={
                        channel.id
                      }
                      value={
                        channel.id
                      }
                    >
                      {
                        channel.label
                      }
                    </option>
                  )
                )}
              </select>
            </div>

            {selectedChannel && (
              <div
                style={{
                  marginTop: 10,
                }}
              >
                <label
                  className="font13"
                  style={{
                    color:
                      '#4a5568',
                    marginBottom:
                      6,
                    display:
                      'flex',
                    alignItems:
                      'center',
                    gap: 8,
                  }}
                >
                  <img
                    src={
                      selectedChannel.icon
                    }
                    alt=""
                    width={16}
                    height={16}
                    style={{
                      objectFit:
                        'contain',
                    }}
                  />

                  {
                    selectedChannel.oauthLabel
                  }
                </label>

                {appsLoading ? (
                  <div
                    style={{
                      fontSize: 12,
                      color:
                        '#64748b',
                      padding:
                        '8px 0',
                    }}
                  >
                    Loading{' '}
                    {
                      selectedChannel.label
                    }{' '}
                    OAuth connections…
                  </div>
                ) : connectedApps.length ===
                  0 ? (
                  <div
                    style={{
                      padding:
                        '10px 12px',
                      borderRadius:
                        10,
                      border:
                        '1px dashed #dbe4f0',
                      color:
                        '#94a3b8',
                      fontSize: 12,
                      background:
                        '#f8fafc',
                    }}
                  >
                    No{' '}
                    {
                      selectedChannel.oauthLabel
                    }{' '}
                    connection found.
                    Connect{' '}
                    {
                      selectedChannel.label
                    }{' '}
                    in Agent Apps
                    first.
                  </div>
                ) : (
                  <>
                    <select
                      className="form-select font13"
                      required={
                        !!selectedChannel
                      }
                      value={
                        selectedOAuth?.appId ||
                        ''
                      }
                      onChange={(
                        event
                      ) =>
                        setOAuthApp(
                          selectedChannel.id,
                          event.target
                            .value
                        )
                      }
                      style={{
                        width:
                          '100%',
                        padding:
                          '9px 12px',
                        borderRadius:
                          10,
                        fontSize: 13,
                        border:
                          oauthRequired
                            ? '1px solid #dc2626'
                            : '1px solid #e2e8f0',
                        background:
                          '#fff',
                      }}
                    >
                      <option value="">
                        Select{' '}
                        {
                          selectedChannel.label
                        }{' '}
                        OAuth
                      </option>

                      {connectedApps.map(
                        (app) => (
                          <option
                            key={
                              app.id
                            }
                            value={
                              app.id
                            }
                          >
                            {app.name ||
                              app.displayName ||
                              selectedChannel.label}
                            {app.provider
                              ? ` (${app.provider})`
                              : ''}
                          </option>
                        )
                      )}
                    </select>

                    {oauthRequired && (
                      <div
                        style={{
                          color:
                            '#dc2626',
                          fontSize: 11,
                          marginTop: 4,
                        }}
                      >
                        OAuth connection
                        is required.
                      </div>
                    )}
                  </>
                )}

                {/*
                 * Recipient is now displayed
                 * for BOTH Slack and Gmail.
                 */}
                <>
                  <label
                    className="font13"
                    style={{
                      color:
                        '#4a5568',
                      marginTop: 10,
                      marginBottom:
                        6,
                      display:
                        'block',
                    }}
                  >
                    Recipient
                  </label>

                  <input
                    className="form-control font13"
                    required={
                      !!selectedChannel
                    }
                    value={
                      node.data
                        .approvalRecipient ||
                      ''
                    }
                    onChange={(
                      event
                    ) =>
                      setApprovalRecipient(
                        event.target
                          .value
                      )
                    }
                    placeholder={
                      selectedChannel.id ===
                        'slack'
                        ? 'Slack user or channel ID (e.g. U0AHBKQM6TA)'
                        : 'Gmail recipient email address'
                    }
                    style={{
                      borderRadius:
                        10,
                      fontSize: 13,
                      border:
                        recipientRequired
                          ? '1px solid #dc2626'
                          : '1px solid #e2e8f0',
                    }}
                  />

                  {recipientRequired && (
                    <div
                      style={{
                        color:
                          '#dc2626',
                        fontSize: 11,
                        marginTop: 4,
                      }}
                    >
                      Recipient is required.
                    </div>
                  )}
                </>
              </div>
            )}
          </div>
        )}
      </div>

      {previewMeta ? (
        <PolicyJsonModal
          data={
            previewLoading
              ? undefined
              : previewDocument
          }
          jsonText=""
          loading={
            previewLoading
          }
          error={
            previewLoading
              ? null
              : previewError
          }
          title={
            previewMeta.name
          }
          subtitle={
            previewMeta.s3Path
              ? UtilService.toPolicySyncPath(
                previewMeta.s3Path
              )
              : 'Policy document'
          }
          onClose={
            closePreview
          }
        />
      ) : null}
    </div>
  );
}